import React from 'react';
import type { Fields } from '@puckeditor/core';

import { clampPercent, createOpacitySlider } from './opacity-slider';
import { resolveSurfaceColor } from './tokens';
import type { BackgroundValue } from './widgets/background-field';

export const DEFAULT_BACKGROUND_OPACITY = 100;

const COMPLETE_HEX = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function normalizeBackgroundOpacity(value: unknown): number {
  return clampPercent(value, DEFAULT_BACKGROUND_OPACITY);
}

const BackgroundOpacityField = createOpacitySlider({
  testIdPrefix: 'background-opacity',
  fallback: DEFAULT_BACKGROUND_OPACITY,
});

/** One shared top-level field descriptor for every component that supports custom background opacity. */
export const BACKGROUND_OPACITY_FIELD = {
  type: 'custom' as const,
  label: 'Background color opacity',
  render: BackgroundOpacityField,
  ai: {
    schema: { type: 'number' as const, minimum: 0, maximum: 100 },
    instructions: 'Opacity percentage for a custom background color only. Use a number from 0 to 100.',
  },
};

export function backgroundValueColor(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return (value as { color?: unknown }).color;
}

/** Theme roles are intentionally excluded: their opacity remains controlled by the merchant theme. */
export function isCustomBackgroundColor(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('#');
}

/**
 * Keeps the opacity field in its declared position for custom colors and omits it otherwise.
 * Returning a filtered field map never writes or clears the hidden saved value.
 */
export function resolveBackgroundOpacityFields<Props extends { backgroundOpacity: number }>(
  backgroundColor: unknown,
  fields: Fields<Props>
): Fields<Props> {
  if (isCustomBackgroundColor(backgroundColor)) return fields;

  return Object.fromEntries(Object.entries(fields).filter(([name]) => name !== 'backgroundOpacity')) as Fields<Props>;
}

function expandHexPair(value: string): string {
  return value.length === 1 ? `${value}${value}` : value;
}

/**
 * Resolves a surface color and applies alpha only when the stored color is a valid custom hex.
 * The 100% path returns the existing resolver output verbatim to preserve current production output.
 */
export function resolveBackgroundColorWithOpacity(color: unknown, opacity: unknown): string {
  const stringColor = typeof color === 'string' ? color : undefined;
  const resolvedColor = resolveSurfaceColor(stringColor);
  const normalizedOpacity = normalizeBackgroundOpacity(opacity);

  if (normalizedOpacity === DEFAULT_BACKGROUND_OPACITY || !stringColor || !COMPLETE_HEX.test(stringColor)) {
    return resolvedColor;
  }

  const hex = stringColor.slice(1);
  const short = hex.length === 3 || hex.length === 4;
  const red = parseInt(expandHexPair(short ? hex[0] : hex.slice(0, 2)), 16);
  const green = parseInt(expandHexPair(short ? hex[1] : hex.slice(2, 4)), 16);
  const blue = parseInt(expandHexPair(short ? hex[2] : hex.slice(4, 6)), 16);
  const embeddedAlphaPair = short ? hex[3] : hex.slice(6, 8);
  const embeddedAlpha = embeddedAlphaPair ? parseInt(expandHexPair(embeddedAlphaPair), 16) / 255 : 1;
  const resolvedAlpha = Number((embeddedAlpha * (normalizedOpacity / 100)).toFixed(4));

  return `rgba(${red}, ${green}, ${blue}, ${resolvedAlpha})`;
}

/** Applies the color-only opacity to a BackgroundValue's custom color. The image and its overlay are separate. */
export function backgroundStyleWithOpacity(
  background: BackgroundValue | undefined,
  opacity: unknown
): React.CSSProperties {
  if (!background) return {};
  return { backgroundColor: resolveBackgroundColorWithOpacity(background.color, opacity) };
}
